import { useTranslation } from 'react-i18next';
import { useApplicationContext } from '../../../../core/contexts/ApplicationContext';
import Filter, { defaultFormatLabel } from '../../../../core/components/Filter.tsx';
import * as React from 'react';
import { camelCase } from 'lodash';
import DropdownMenu from '../../../../core/components/DropdownMenu';
import { IFilter } from '../../../../core/utils/filterQuery.ts';
import { UserFilterName } from '../../../constants.ts';

const AssignToFilter = ({ filters, onFiltersChange, activeChannel, channels, filterName }) => {
    const { t } = useTranslation('assistance');
    const { user } = useApplicationContext();

    const filter = filters.find((f: IFilter) => f.name === filterName);
    const actualSelected = filter ? (Array.isArray(filter.value) ? filter.value : [filter.value]) : [];

    const handleSelectedChange = (selected: string[]) => {
        let newFilters = filters.filter((f: any) => f.name !== filterName);
        if (selected.length) {
            newFilters.push({ name: filterName, value: selected, exclude: false, expression: 'eq' });
        }
        onFiltersChange(newFilters);
    };

    const channelsToFilter = activeChannel ? [activeChannel] : channels;

    const allChannelUsers: any[] = channelsToFilter.flatMap((channel) =>
        channel.team?.users ? channel.team.users.filter((u) => u.email !== user.email) : []
    );
    const uniqueChannelUsers = Array.from(new Map(allChannelUsers.map((item) => [item.email, item])).values());

    uniqueChannelUsers.sort((a, b) => a.firstName.localeCompare(b.firstName));
    uniqueChannelUsers.unshift(user);

    const userOptions = uniqueChannelUsers.map((user) => ({
        value: user.email,
        label: `${user.firstName} ${user.lastName}`,
    }));

    const AUTOMATED_OPTION = { label: t('overview.archivedByAutomated'), value: 'automated' };
    const NOT_ASSIGNED_OPTION = { label: t('overview.recordRow.actions.notAssigned'), value: 'unassigned' };

    const formatLabel = ({ defaultLabel, allLabel, options, selected, searchValue, multiple }) => {
        let allFilterOptions = filterName === UserFilterName.ASSISTED_BY ? [...options, AUTOMATED_OPTION] : options;
        if (filterName === UserFilterName.ASSIGNED_TO) {
            allFilterOptions = [...options, NOT_ASSIGNED_OPTION];
            if (actualSelected.length > 1) {
                return `${defaultLabel} (${actualSelected.length})`;
            }
        }

        return defaultFormatLabel({
            defaultLabel,
            allLabel,
            options: allFilterOptions,
            selected,
            searchValue,
            multiple,
        });
    };

    const typeDisplay = camelCase(filterName);

    const extraContent = (
        <div className="sticky bottom-0 w-full pt-1 border-t border-solid border-line-strong bg-surface-weak">
            <DropdownMenu.CheckboxItem
                className="hover:border-none"
                onCheckedChange={(checked) => {
                    if (checked) {
                        handleSelectedChange([...actualSelected, NOT_ASSIGNED_OPTION.value]);
                    } else {
                        handleSelectedChange(actualSelected.filter((value) => value !== NOT_ASSIGNED_OPTION.value));
                    }
                }}
                checked={actualSelected.includes(NOT_ASSIGNED_OPTION.value)}
                onSelect={(e) => e.preventDefault()}
            >
                <span>{NOT_ASSIGNED_OPTION.label}</span>
            </DropdownMenu.CheckboxItem>
        </div>
    );

    return (
        <Filter
            allLabel={t(`overview.filters.${typeDisplay}.all`)}
            label={t(`overview.filters.${typeDisplay}.label`)}
            options={userOptions}
            selected={actualSelected}
            onSelectedChange={handleSelectedChange}
            multiple
            showSearch={true}
            extraContent={extraContent}
            formatLabel={formatLabel}
            showMultipleCount={!(filterName === UserFilterName.ASSIGNED_TO)}
        />
    );
};

export default AssignToFilter;
